/**
 * Serviço de banco SQLite no frontend (sql.js via CDN) para armazenar presets de filtros Hinova.
 * Os dados são persistidos em localStorage e associados ao usuário logado (user_id).
 * O sql.js é carregado dinamicamente do CDN para evitar erro de compilação (class properties no Babel).
 */
import { v4 as uuidv4 } from 'uuid';
import type { HinovaFilterPresetFilters, HinovaFilterPreset } from '~/types';

const STORAGE_KEY = 'hinova_filter_presets_db';
const TABLE_NAME = 'hinova_filter_presets';
const SQL_JS_CDN = 'https://sql.js.org/dist/sql-wasm.js';

interface SqlJsStatement {
  bind(params: unknown[]): void;
  step(): boolean;
  get(): unknown;
  free(): void;
}

interface SqlJsDb {
  run(sql: string, params?: unknown[]): void;
  prepare(sql: string): SqlJsStatement;
  export(): Uint8Array;
}

interface SqlJsNamespace {
  Database: new (data?: Uint8Array) => SqlJsDb;
}

declare global {
  interface Window {
    initSqlJs?: (config: { locateFile?: (file: string) => string }) => Promise<SqlJsNamespace>;
  }
}

let dbInstance: SqlJsDb | null = null;
let initPromise: Promise<SqlJsDb> | null = null;

function loadSqlJs(): Promise<SqlJsNamespace> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Not in browser'));
  }
  const w = window as Window & { initSqlJs?: (config: object) => Promise<SqlJsNamespace> };
  if (w.initSqlJs) {
    return w.initSqlJs({
      locateFile: (file: string) => `https://sql.js.org/dist/${file}`,
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SQL_JS_CDN;
    script.async = true;
    script.onload = () => {
      const init = (w as Window & { initSqlJs: (config: object) => Promise<SqlJsNamespace> }).initSqlJs;
      if (init) {
        init({ locateFile: (file: string) => `https://sql.js.org/dist/${file}` }).then(resolve).catch(reject);
      } else {
        reject(new Error('sql.js não carregou'));
      }
    };
    script.onerror = () => reject(new Error('Falha ao carregar sql.js'));
    document.head.appendChild(script);
  });
}

async function initDb(): Promise<SqlJsDb> {
  if (dbInstance) return dbInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const SQL = await loadSqlJs();

    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    const data = saved ? new Uint8Array(JSON.parse(saved)) : undefined;
    dbInstance = new SQL.Database(data);

    dbInstance.run(`
      CREATE TABLE IF NOT EXISTS ${TABLE_NAME} (
        id TEXT PRIMARY KEY,
        user_id TEXT NOT NULL,
        name TEXT NOT NULL,
        filters TEXT NOT NULL,
        created_at INTEGER NOT NULL,
        updated_at INTEGER NOT NULL
      )
    `);
    persist();
    return dbInstance;
  })();

  return initPromise;
}

function persist(): void {
  if (!dbInstance || typeof localStorage === 'undefined') return;
  try {
    const data = dbInstance.export();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(data)));
  } catch (e) {
    console.warn('hinovaFilterPresetsDb: falha ao persistir no localStorage', e);
  }
}

function rowToPreset(row: string[]): HinovaFilterPreset {
  const [id, user_id, name, filtersJson, created_at, updated_at] = row;
  return {
    id,
    user_id,
    name,
    filters: JSON.parse(filtersJson) as HinovaFilterPresetFilters,
    created_at: Number(created_at),
    updated_at: Number(updated_at),
  };
}

/**
 * Retorna o banco inicializado. Inicializa de forma lazy na primeira chamada.
 */
export async function getDb(): Promise<SqlJsDb> {
  return initDb();
}

/**
 * Cria um novo preset de filtro para o usuário.
 */
export async function createPreset(
  userId: string,
  name: string,
  filters: HinovaFilterPresetFilters,
): Promise<HinovaFilterPreset> {
  const db = await getDb();
  const id = uuidv4();
  const now = Date.now();
  const filtersJson = JSON.stringify(filters);

  db.run(
    `INSERT INTO ${TABLE_NAME} (id, user_id, name, filters, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)`,
    [id, userId, name, filtersJson, now, now],
  );
  persist();

  return { id, user_id: userId, name, filters, created_at: now, updated_at: now };
}

/**
 * Lista todos os presets do usuário.
 */
export async function listPresets(userId: string): Promise<HinovaFilterPreset[]> {
  const db = await getDb();
  const stmt = db.prepare(
    `SELECT id, user_id, name, filters, created_at, updated_at FROM ${TABLE_NAME} WHERE user_id = ? ORDER BY updated_at DESC`,
  );
  stmt.bind([userId]);
  const presets: HinovaFilterPreset[] = [];
  while (stmt.step()) {
    const row = stmt.get();
    presets.push(rowToPreset(row as string[]));
  }
  stmt.free();
  return presets;
}

/**
 * Atualiza um preset existente (apenas do mesmo usuário).
 */
export async function updatePreset(
  id: string,
  userId: string,
  data: { name?: string; filters?: HinovaFilterPresetFilters },
): Promise<HinovaFilterPreset | null> {
  const db = await getDb();
  const stmt = db.prepare(
    `SELECT id, user_id, name, filters, created_at, updated_at FROM ${TABLE_NAME} WHERE id = ? AND user_id = ?`,
  );
  stmt.bind([id, userId]);
  let preset: HinovaFilterPreset | null = null;
  if (stmt.step()) {
    preset = rowToPreset(stmt.get() as string[]);
  }
  stmt.free();
  if (!preset) return null;
  const name = data.name ?? preset.name;
  const filters = data.filters ?? preset.filters;
  const now = Date.now();
  const filtersJson = JSON.stringify(filters);

  db.run(
    `UPDATE ${TABLE_NAME} SET name = ?, filters = ?, updated_at = ? WHERE id = ? AND user_id = ?`,
    [name, filtersJson, now, id, userId],
  );
  persist();

  return { ...preset, name, filters, updated_at: now };
}

/**
 * Remove um preset (apenas se pertencer ao usuário).
 */
export async function deletePreset(id: string, userId: string): Promise<boolean> {
  const db = await getDb();
  db.run(`DELETE FROM ${TABLE_NAME} WHERE id = ? AND user_id = ?`, [id, userId]);
  persist();
  return true;
}

/**
 * Obtém um preset por id (apenas se pertencer ao usuário).
 */
export async function getPreset(id: string, userId: string): Promise<HinovaFilterPreset | null> {
  const db = await getDb();
  const stmt = db.prepare(
    `SELECT id, user_id, name, filters, created_at, updated_at FROM ${TABLE_NAME} WHERE id = ? AND user_id = ?`,
  );
  stmt.bind([id, userId]);
  const preset = stmt.step() ? rowToPreset(stmt.get() as string[]) : null;
  stmt.free();
  return preset;
}
